import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Luggage, Gauge, Fuel } from 'lucide-react';
import { RentalVehicle } from '@/hooks/useRentalVehicles';

interface VehicleCardProps {
  vehicle: RentalVehicle;
}

export const VehicleCard = ({ vehicle }: VehicleCardProps) => {
  return (
    <Link to={`/car-rental/${vehicle.slug}`}>
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 group h-full">
        <div className="aspect-video overflow-hidden relative">
          <img
            src={vehicle.main_image_url || '/placeholder.svg'}
            alt={vehicle.vehicle_name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          {vehicle.is_featured && (
            <Badge className="absolute top-4 right-4 bg-primary">
              Featured
            </Badge>
          )}
        </div>
        <CardContent className="p-6">
          <div className="mb-3">
            <h3 className="text-xl font-bold mb-1 group-hover:text-primary transition-colors">
              {vehicle.vehicle_name}
            </h3>
            <p className="text-sm text-muted-foreground">
              {vehicle.brand} {vehicle.model} {vehicle.year}
            </p>
          </div>

          {vehicle.short_description && (
            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              {vehicle.short_description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span>{vehicle.passengers} Passengers</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Luggage className="w-4 h-4 text-muted-foreground" />
              <span>{vehicle.luggage} Bags</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="w-4 h-4 text-muted-foreground" />
              <span className="capitalize">{vehicle.transmission}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Fuel className="w-4 h-4 text-muted-foreground" />
              <span className="capitalize">{vehicle.fuel_type}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <div>
              <p className="text-sm text-muted-foreground">From</p>
              <p className="text-2xl font-bold text-primary">
                ${vehicle.daily_rate}
                <span className="text-sm font-normal text-muted-foreground">/day</span>
              </p>
            </div>
            <Badge variant={vehicle.is_available ? 'default' : 'secondary'}>
              {vehicle.is_available ? 'Available' : 'Unavailable'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};